import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type RefObject,
} from 'react';
import { Link, useParams } from 'react-router-dom';
import type { SessionDetail } from '@opsrev/shared';
import {
  getSession,
  reprocessSession,
  sessionExportUrl,
  sessionVideoUrl,
} from '../api';
import { useAsync } from '../useAsync';
import { absoluteTime, formatDuration } from '../format';
import { EmptyState, ErrorState, SessionStatusChip, Spinner } from '../components/Status';
import {
  IntegrationMapTable,
  ObjectivePanel,
  SkillsList,
  SopList,
} from '../components/Distillation';
import { EventTimeline } from '../components/EventTimeline';

function ReprocessButton({
  sessionId,
  status,
  onChanged,
}: {
  sessionId: string;
  status: SessionDetail['status'];
  onChanged: () => void;
}): ReactElement {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isProcessing = status === 'processing';

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      await reprocessSession(sessionId);
      onChanged(); // parent kicks off polling
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reprocess.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="reprocess">
      <button className="btn" onClick={run} disabled={busy || isProcessing}>
        {busy ? 'Starting…' : isProcessing ? 'Processing…' : 'Reprocess'}
      </button>
      {error ? <span className="error-text inline">{error}</span> : null}
    </div>
  );
}

function VideoPanel({
  session,
  videoRef,
  seek,
}: {
  session: SessionDetail;
  videoRef: RefObject<HTMLVideoElement | null>;
  seek: (sec: number) => void;
}): ReactElement {
  const [currentTime, setCurrentTime] = useState(0);

  const hasVideo = session.media.videoKey !== null;

  return (
    <div className="video-panel">
      {hasVideo ? (
        <video
          ref={videoRef}
          className="session-video"
          src={sessionVideoUrl(session.id)}
          controls
          preload="metadata"
          onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        />
      ) : (
        <div className="video-missing">
          <EmptyState title="No video" hint="This session has no screen recording." />
        </div>
      )}
      <EventTimeline
        events={session.events}
        durationMs={session.metadata.durationMs}
        currentTimeSec={currentTime}
        onSeek={seek}
      />
    </div>
  );
}

export function SessionDetailPage(): ReactElement {
  const { id = '' } = useParams();
  const loader = useCallback(() => getSession(id), [id]);
  const { data, loading, error, reload } = useAsync<SessionDetail>(loader, [id]);

  // Lifted to the page so both the EventTimeline and the SOP seek the SAME <video>.
  const videoRef = useRef<HTMLVideoElement>(null);
  const seek = useCallback((sec: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = sec;
    void v.play().catch(() => undefined);
  }, []);

  // Poll while processing (e.g. after a reprocess) until it settles.
  const polling = data?.status === 'processing';
  useEffect(() => {
    if (!polling) return;
    const t = setInterval(() => reload(), 3000);
    return () => clearInterval(t);
  }, [polling, reload]);

  if (loading && !data) return <Spinner label="Loading session…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return <EmptyState title="Session not found" />;

  const { metadata, analysis, status } = data;

  return (
    <div className="page">
      <div className="breadcrumb">
        <Link to="/">Clients</Link>
        <span>/</span>
        <Link to={`/clients/${data.clientId}`}>Client</Link>
        <span>/</span>
        <span>{metadata.taskTitle || 'Session'}</span>
      </div>

      <div className="page-head">
        <div>
          <h1>{metadata.taskTitle || '(untitled session)'}</h1>
          <p className="muted">
            <SessionStatusChip status={status} /> {metadata.userRole || 'Unknown role'} ·{' '}
            {metadata.platform ?? 'unknown'} · {formatDuration(metadata.durationMs)} ·{' '}
            {absoluteTime(metadata.createdAt)}
          </p>
        </div>
        <div className="actions-row">
          <a className="btn" href={sessionExportUrl(data.id)} download>
            Export (.md)
          </a>
          <ReprocessButton sessionId={data.id} status={status} onChanged={reload} />
        </div>
      </div>

      {metadata.taskDescription ? (
        <p className="task-desc">{metadata.taskDescription}</p>
      ) : null}

      {status === 'error' && analysis?.error ? (
        <div className="callout error">
          <strong>Processing failed:</strong> {analysis.error}
        </div>
      ) : null}

      {analysis?.objective ? (
        <section className="section">
          <ObjectivePanel objective={analysis.objective} />
        </section>
      ) : null}

      <section className="section">
        <h2>Recording</h2>
        <VideoPanel session={data} videoRef={videoRef} seek={seek} />
      </section>

      {data.transcript ? (
        <section className="section">
          <h2>Transcript</h2>
          <div className="card transcript">{data.transcript}</div>
        </section>
      ) : null}

      <section className="section">
        <h2>Integration / API map</h2>
        {status === 'processing' ? (
          <Spinner label="Analysis in progress…" />
        ) : analysis ? (
          <div className="card">
            <IntegrationMapTable entries={analysis.integrationMap} />
          </div>
        ) : (
          <EmptyState title="No analysis yet" hint="Run reprocess to generate findings." />
        )}
      </section>

      {analysis && analysis.skills && analysis.skills.length > 0 ? (
        <section className="section">
          <h2>Candidate skills</h2>
          <SkillsList skills={analysis.skills} />
        </section>
      ) : null}

      <section className="section">
        <h2>SOP</h2>
        {status === 'processing' ? (
          <Spinner label="Analysis in progress…" />
        ) : analysis ? (
          <>
            {analysis.summary ? <p className="summary">{analysis.summary}</p> : null}
            <div className="card">
              <SopList steps={analysis.sop} seek={seek} />
            </div>
          </>
        ) : (
          <EmptyState title="No SOP yet" />
        )}
      </section>
    </div>
  );
}
